/**
 * Selection Toolbar Component
 * Displays when transactions are selected and provides bulk actions
 */

import {
  AlertTriangle,
  ArrowDownToLine,
  Briefcase,
  ChevronDown,
  HandCoins,
  History,
  ListTodo,
  Mail,
  Receipt,
  StickyNote,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { TransactionTotals } from '@/lib/export-utils';
import { formatCurrency } from '@/lib/utils';
import type { Transaction } from '@/types/transaction';

interface SelectionToolbarProps {
  selectedCount: number;
  totals: TransactionTotals;
  selectedTransactions: Transaction[];
  baseCurrency?: string;
  onClearSelection: () => void;
  onExportCurrentView: () => void;
  onExportAllColumns: () => void;
  onSendReminders: () => void;
  onAllocateReceipt: () => void;
  onSendCopyInvoice?: () => void;
  onCreateCollectionCase?: () => void;
  onCreatePromise?: () => void;
  onCreateDispute?: () => void;
  onAddNote?: () => void;
  onViewAllocationHistory?: () => void;
  onCreateTask?: () => void;
}

export function SelectionToolbar({
  selectedCount,
  totals,
  selectedTransactions,
  baseCurrency = 'GBP',
  onClearSelection,
  onExportCurrentView,
  onExportAllColumns,
  onSendReminders,
  onAllocateReceipt,
  onSendCopyInvoice,
  onCreateCollectionCase,
  onCreatePromise,
  onCreateDispute,
  onAddNote,
  onViewAllocationHistory,
  onCreateTask,
}: Readonly<SelectionToolbarProps>) {
  // Safety check - if no transactions or invalid data, don't render
  if (!selectedTransactions || selectedTransactions.length === 0 || !totals) {
    return null;
  }

  // Check if selected transactions contain receipts
  const hasReceipts = selectedTransactions.some((t) => t?.transactionType === 'receipt');

  // Check if selected transactions contain ONLY unallocated receipts
  const hasOnlyUnallocatedReceipts =
    selectedTransactions.length > 0 &&
    selectedTransactions.every(
      (t) => t?.transactionType === 'receipt' && t?.outstandingAmount !== undefined && t?.outstandingAmount !== 0,
    );

  // Check if selected transactions contain overdue invoices
  const hasOverdueInvoices = selectedTransactions.some(
    (t) => t?.transactionType === 'invoice' && t?.status === 'overdue',
  );

  // Check if selected transactions contain invoices (for collection cases)
  const hasInvoices = selectedTransactions.some((t) => t?.transactionType === 'invoice');

  // Check if selected transactions contain invoices or receipts (for cases and promises)
  const hasInvoicesOrReceipts = selectedTransactions.some(
    (t) => t?.transactionType === 'invoice' || t?.transactionType === 'receipt',
  );

  // Check if all selected transactions belong to the same customer account
  const isSameCustomer = (() => {
    if (selectedTransactions.length === 0) return false;
    const uniqueCustomerIds = new Set(selectedTransactions.map((t) => t?.customerId).filter(Boolean));
    return uniqueCustomerIds.size === 1;
  })();

  // Check if "Send Copy Invoice" should be shown
  // Show for any transaction type as long as they belong to a single customer
  const shouldShowSendCopyInvoice = (() => {
    if (!onSendCopyInvoice) return false;

    // Must be from the same customer
    if (!isSameCustomer) return false;

    return true;
  })();

  // Allocation history: show for single selection with allocations,
  // or multi-select when all share the same non-empty AllocationRef
  const shouldShowAllocationHistory = (() => {
    if (selectedTransactions.length === 0) return false;
    if (selectedTransactions.length === 1) {
      return (selectedTransactions[0]?.allocationCount ?? 0) > 0;
    }
    // Multi-select: all must have allocationCount > 0 and the same allocationRef
    const firstRef = selectedTransactions[0]?.allocationRef;
    if (!firstRef) return false;
    return selectedTransactions.every((t) => (t?.allocationCount ?? 0) > 0 && t?.allocationRef === firstRef);
  })();

  try {
    return (
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
        <div className="bg-primary text-primary-foreground rounded-lg shadow-2xl border border-primary/20">
          <div className="flex items-center gap-4 px-6 py-4">
            {/* Selection Info */}
            <div className="flex items-center gap-3 border-r border-primary-foreground/20 pr-4">
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <Badge
                    variant="secondary"
                    className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30"
                  >
                    {selectedCount}
                  </Badge>
                  <span className="font-medium">{selectedCount === 1 ? 'transaction' : 'transactions'} selected</span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-sm text-primary-foreground/80">
                  <span>
                    Total:{' '}
                    <span className="font-semibold">
                      {formatCurrency(totals?.totalAmount || 0, totals?.currency || baseCurrency)}
                    </span>
                  </span>
                  {(totals?.totalOutstanding || 0) > 0 && (
                    <>
                      <span>•</span>
                      <span>
                        Outstanding:{' '}
                        <span className="font-semibold">
                          {formatCurrency(totals?.totalOutstanding || 0, totals?.currency || baseCurrency)}
                        </span>
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" size="sm" className="gap-2">
                    <ArrowDownToLine className="h-4 w-4" />
                    Export
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={onExportCurrentView}>Export Current View</DropdownMenuItem>
                  <DropdownMenuItem onClick={onExportAllColumns}>Export All Columns</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {isSameCustomer && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="sm" className="gap-2">
                      Actions
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {/* Collection Actions - Only show when all transactions belong to the same customer */}
                    {hasInvoicesOrReceipts && isSameCustomer && onCreateCollectionCase && (
                      <DropdownMenuItem onClick={onCreateCollectionCase} className="gap-2">
                        <Briefcase className="h-4 w-4" />
                        Create Collection Case
                      </DropdownMenuItem>
                    )}
                    {hasInvoicesOrReceipts && isSameCustomer && onCreatePromise && (
                      <DropdownMenuItem onClick={onCreatePromise} className="gap-2">
                        <HandCoins className="h-4 w-4" />
                        Create Promise to Pay
                      </DropdownMenuItem>
                    )}
                    {hasInvoices && isSameCustomer && onCreateDispute && (
                      <DropdownMenuItem onClick={onCreateDispute} className="gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Flag as Disputed
                      </DropdownMenuItem>
                    )}
                    {isSameCustomer && onAddNote && (
                      <DropdownMenuItem onClick={onAddNote} className="gap-2">
                        <StickyNote className="h-4 w-4" />
                        Add Note
                      </DropdownMenuItem>
                    )}
                    {isSameCustomer && onCreateTask && (
                      <DropdownMenuItem onClick={onCreateTask} className="gap-2">
                        <ListTodo className="h-4 w-4" />
                        Create Task
                      </DropdownMenuItem>
                    )}

                    {((hasInvoicesOrReceipts && isSameCustomer) ||
                      (isSameCustomer && onAddNote) ||
                      (isSameCustomer && onCreateTask)) &&
                      (hasOverdueInvoices || shouldShowSendCopyInvoice || hasReceipts) && <DropdownMenuSeparator />}

                    {/* Communication Actions */}
                    {shouldShowSendCopyInvoice && (
                      <DropdownMenuItem onClick={onSendCopyInvoice} className="gap-2">
                        <Mail className="h-4 w-4" />
                        Send Email
                      </DropdownMenuItem>
                    )}

                    {hasOnlyUnallocatedReceipts && (hasOverdueInvoices || shouldShowSendCopyInvoice) && (
                      <DropdownMenuSeparator />
                    )}

                    {/* Receipt Actions - Only show for unallocated receipts */}
                    {hasOnlyUnallocatedReceipts && (
                      <DropdownMenuItem onClick={onAllocateReceipt} className="gap-2">
                        <Receipt className="h-4 w-4" />
                        Allocate Receipt
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Allocation History - Show when transaction(s) have allocations (same ref for multi-select) */}
              {shouldShowAllocationHistory && onViewAllocationHistory && (
                <Button variant="secondary" size="sm" onClick={onViewAllocationHistory} className="gap-2">
                  <History className="h-4 w-4" />
                  Allocation History
                </Button>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={onClearSelection}
                className="gap-2 hover:bg-primary-foreground/20"
              >
                <X className="h-4 w-4" />
                Clear
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    console.error('Error rendering SelectionToolbar:', error);
    return null;
  }
}
